import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Users,
  Shield,
  Eye,
  Zap,
  Clock,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Scale,
  HelpCircle,
  BookOpen,
} from 'lucide-react';
import { HierarchicalGovernanceChart } from './HierarchicalGovernanceChart';
import { CustomGovernanceModal } from './CustomGovernanceModal';
import { GlossaryTrigger } from '../glossary';

export function LegacyComparison() {
  const [activeScenario, setActiveScenario] = useState('budget');
  const [isGovernanceModalOpen, setIsGovernanceModalOpen] = useState(false);

  return (
    <div className="space-y-6 xs:space-y-8">
      {/* Section Header */}
      <div className="text-center space-y-3 xs:space-y-4">
        <h2 className="text-2xl xs:text-3xl sm:text-4xl font-bold bg-linear-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-tight">
          Community Governance Framework
        </h2>
        <p className="text-base xs:text-lg sm:text-xl text-muted-foreground max-w-4xl mx-auto sm:px-6">
          This isn't a rigid system, it's a configurable digital framework that adapts to whatever
          governance model your community chooses. Every community can customize how decisions flow,
          who has override authority, and whether the community votes directly or through
          representatives.
        </p>
        <div className="bg-blue-50 border border-blue-200 p-3 xs:p-4 rounded-lg max-w-3xl mx-auto">
          <p className="text-sm text-blue-800">
            <strong>Malleable by Design:</strong> Your community can choose from multiple governance
            models Community Elect as overrider, community subpanel as override authority, full
            popular vote systems, or hybrid approaches. The technology adapts to your community's
            preferences.
          </p>
        </div>

        {/* Glossary Notice */}
        <div className="flex items-center justify-center gap-4 pt-3 xs:pt-4 sm:px-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 xs:p-4 flex items-center gap-2 xs:gap-3 w-full max-w-sm xs:max-w-md sm:max-w-lg">
            <HelpCircle className="w-4 h-4 xs:w-5 xs:h-5 text-yellow-600 shrink-0" />
            <div className="text-xs xs:text-sm text-yellow-800">
              <strong>New to these terms?</strong> No worries!
            </div>
            <GlossaryTrigger
              variant="help"
              className="text-yellow-700 hover:text-yellow-900 shrink-0"
              showModal={false}
            />
          </div>
        </div>
      </div>

      {/* Hierarchical Governance Chart */}
      <HierarchicalGovernanceChart />

      {/* Bridge Section */}
      <div className="text-center space-y-3 xs:space-y-4 pt-6 xs:pt-8 sm:px-6">
        <h3 className="text-xl xs:text-2xl font-bold">
          Transform Your Community's Decision-Making
        </h3>
        <p className="text-base xs:text-lg text-muted-foreground max-w-3xl mx-auto">
          Stop accepting slow, frustrating governance as "just how HOAs work." See how communities
          subpanel systems resolve issues faster, with higher satisfaction, and complete
          transparency while maintaining the digital democratic voice you want.
        </p>
      </div>

      {/* Comparison Tabs */}
      <Tabs defaultValue="structure" className="space-y-4 xs:space-y-6">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 gap-0.5 sm:gap-0 h-auto sm:h-10">
          <TabsTrigger value="structure" className="text-xs sm:text-sm px-2 py-2 sm:px-3 sm:py-1.5">
            Structure
          </TabsTrigger>
          <TabsTrigger value="decisions" className="text-xs sm:text-sm px-2 py-2 sm:px-3 sm:py-1.5">
            <span className="hidden sm:inline">Decision Making</span>
            <span className="sm:hidden">Decisions</span>
          </TabsTrigger>
          <TabsTrigger
            value="transparency"
            className="text-xs sm:text-sm px-2 py-2 sm:px-3 sm:py-1.5"
          >
            <span className="hidden sm:inline">Transparency</span>
            <span className="sm:hidden">Open</span>
          </TabsTrigger>
          <TabsTrigger value="scenarios" className="text-xs sm:text-sm px-2 py-2 sm:px-3 sm:py-1.5">
            <span className="hidden sm:inline">Real Scenarios</span>
            <span className="sm:hidden">Examples</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="structure">
          <StructureComparison />
        </TabsContent>

        <TabsContent value="decisions">
          <DecisionMakingComparison />
        </TabsContent>

        <TabsContent value="transparency">
          <TransparencyComparison />
        </TabsContent>

        <TabsContent value="scenarios">
          <ScenarioComparison
            activeScenario={activeScenario}
            onScenarioChange={setActiveScenario}
          />
        </TabsContent>
      </Tabs>

      {/* Call to Action */}
      <Card className="bg-linear-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="text-center p-4 xs:p-6 sm:p-8">
          <h3 className="text-xl xs:text-2xl font-bold mb-3 xs:mb-4">
            Ready to See Your Community's Transformation?
          </h3>
          <p className="text-sm xs:text-base text-muted-foreground mb-4 xs:mb-6 max-w-2xl mx-auto">
            Every community is different. Use our interactive tools to model how the subpanel system
            would work with your specific governance preferences and see the potential improvements
            in decision speed, transparency, and resident satisfaction.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-linear-to-r from-blue-600 to-purple-600 w-full sm:w-auto"
              onClick={() => setIsGovernanceModalOpen(true)}
            >
              Model Your Community
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Custom Governance Modal */}
      <CustomGovernanceModal
        isOpen={isGovernanceModalOpen}
        onClose={() => setIsGovernanceModalOpen(false)}
      />
    </div>
  );
}

function StructureComparison() {
  const comparisons = [
    {
      aspect: 'Leadership Structure',
      legacy: {
        text: 'Unclear roles, power struggles',
        icon: AlertTriangle,
        color: 'text-red-600',
      },
      modern: {
        text: '1 Community Elect (overrider) + max 3 Community Directors (decision-makers)',
        icon: CheckCircle,
        color: 'text-green-600',
      },
    },
    {
      aspect: 'Decision Making',
      legacy: { text: 'Committee meetings, endless debates', icon: Clock, color: 'text-red-600' },
      modern: {
        text: 'Directors propose, subpanel votes, community can override',
        icon: Zap,
        color: 'text-green-600',
      },
    },
    {
      aspect: 'Override Authority',
      legacy: {
        text: 'Board president makes unilateral decisions',
        icon: AlertTriangle,
        color: 'text-red-600',
      },
      modern: {
        text: 'Community Elect stays behind the fray, overrides only when community requests',
        icon: Shield,
        color: 'text-green-600',
      },
    },
    {
      aspect: 'Community Voice',
      legacy: {
        text: 'Annual meetings, limited input',
        icon: AlertTriangle,
        color: 'text-red-600',
      },
      modern: {
        text: 'Can vote out subpanel members OR request digital override',
        icon: Users,
        color: 'text-green-600',
      },
    },
    {
      aspect: 'Transparency',
      legacy: {
        text: 'Closed meetings, summary minutes',
        icon: AlertTriangle,
        color: 'text-red-600',
      },
      modern: {
        text: 'All decisions tracked to individuals in real-time',
        icon: Eye,
        color: 'text-green-600',
      },
    },
    {
      aspect: 'Governance Model',
      legacy: {
        text: 'Fixed structure, one-size-fits-all',
        icon: AlertTriangle,
        color: 'text-red-600',
      },
      modern: {
        text: 'Configurable - choose your preferred decision-making model',
        icon: Scale,
        color: 'text-green-600',
      },
    },
  ];

  return (
    <div className="grid gap-3 xs:gap-4">
      {comparisons.map((comp, index) => (
        <Card key={index}>
          <CardContent className="p-3 xs:p-4 sm:p-6">
            <div className="grid lg:grid-cols-3 gap-3 xs:gap-4 items-start">
              <div className="font-semibold text-base xs:text-lg mb-2 lg:mb-0">{comp.aspect}</div>

              <div className="flex items-start gap-2 xs:gap-3 p-3 xs:p-4 rounded-lg bg-red-50 border border-red-200">
                <comp.legacy.icon
                  className={`w-4 h-4 xs:w-5 xs:h-5 mt-0.5 ${comp.legacy.color} shrink-0`}
                />
                <div>
                  <div className="text-xs xs:text-sm font-medium text-red-800 mb-1">
                    Traditional HOA
                  </div>
                  <div className="text-xs xs:text-sm text-red-700">{comp.legacy.text}</div>
                </div>
              </div>

              <div className="flex items-start gap-2 xs:gap-3 p-3 xs:p-4 rounded-lg bg-green-50 border border-green-200">
                <comp.modern.icon
                  className={`w-4 h-4 xs:w-5 xs:h-5 mt-0.5 ${comp.modern.color} shrink-0`}
                />
                <div>
                  <div className="text-xs xs:text-sm font-medium text-green-800 mb-1">
                    Subpanel System
                  </div>
                  <div className="text-xs xs:text-sm text-green-700">{comp.modern.text}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function DecisionMakingComparison() {
  const processes: Array<{
    scenario: string;
    legacy: {
      steps: string[];
      time: string;
      satisfaction: string;
    };
    modern: {
      steps: string[];
      time: string;
      satisfaction: string;
      note?: string;
    };
  }> = [
    {
      scenario: 'Road Repair Approval',
      legacy: {
        steps: [
          'Community complaint filed',
          'Board review scheduled (next month)',
          'Committee assessment delays',
          'Board debates contractor selection',
          'Resident notification of decision',
        ],
        time: '3-4 months',
        satisfaction: '35%',
      },
      modern: {
        steps: [
          'Community voices repair need',
          'Community Subpanel reviews request',
          'Director creates resolution',
          'Community Subpanel votes on resolution',
          'Community can override if disagreed*',
        ],
        time: '2-3 weeks',
        satisfaction: '88%',
        note: '*Community can either vote out subpanel members or request Community Elect override via digital vote',
      },
    },
    {
      scenario: 'Budget Approval',
      legacy: {
        steps: [
          '3-month committee review',
          'Board meeting debates',
          'Resident vote',
          'Implementation delays',
        ],
        time: '5 months',
        satisfaction: '40%',
      },
      modern: {
        steps: [
          'Community requests budget changes',
          'Community subpanel reviews needs',
          'Directors create budget resolution',
          'Community Subpanel votes on resolution',
          'Community override options available*',
        ],
        time: '5 weeks',
        satisfaction: '85%',
        note: '*Community Elect stays behind the fray - only overrides when community requests it',
      },
    },
    {
      scenario: 'Maintenance Issue',
      legacy: {
        steps: [
          'Resident complaint',
          'Property manager review',
          'Board review',
          'Vendor selection delays',
        ],
        time: '6-8 weeks',
        satisfaction: '50%',
      },
      modern: {
        steps: [
          'Digital report filed',
          'Community subpanel reviews issue',
          'Director assesses & proposes solution',
          'Community Subpanel votes, Community Elect available for disputes*',
        ],
        time: '1-2 weeks',
        satisfaction: '90%',
        note: '*Community Elect only intervenes when community requests override',
      },
    },
  ];

  return (
    <div className="space-y-4 xs:space-y-6">
      {processes.map((process, index) => (
        <Card key={index}>
          <CardHeader className="p-3 xs:p-4 sm:p-6">
            <CardTitle className="text-base xs:text-lg">{process.scenario}</CardTitle>
          </CardHeader>
          <CardContent className="p-3 xs:p-4 sm:p-6 pt-0">
            <div className="grid lg:grid-cols-2 gap-4 xs:gap-6">
              {/* Legacy Process */}
              <div className="space-y-3 xs:space-y-4">
                <div className="flex items-center gap-2 mb-3 xs:mb-4">
                  <Badge className="bg-red-100 text-red-800 border-red-200 text-xs xs:text-sm">
                    🔴 Traditional HOA
                  </Badge>
                </div>

                <div className="space-y-2 xs:space-y-3">
                  {process.legacy.steps.map((step, stepIndex) => (
                    <div key={stepIndex} className="flex items-start gap-2 xs:gap-3">
                      <div className="w-5 h-5 xs:w-6 xs:h-6 rounded-full bg-red-100 text-red-600 text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">
                        {stepIndex + 1}
                      </div>
                      <div className="text-xs xs:text-sm">{step}</div>
                    </div>
                  ))}
                </div>

                <div className="pt-3 xs:pt-4 border-t flex flex-col xs:flex-row xs:justify-between gap-2 text-xs xs:text-sm">
                  <div>
                    <span className="font-medium">Time:</span> {process.legacy.time}
                  </div>
                  <div>
                    <span className="font-medium">Satisfaction:</span> {process.legacy.satisfaction}
                  </div>
                </div>
              </div>

              {/* Modern Process */}
              <div className="space-y-3 xs:space-y-4">
                <div className="flex items-center gap-2 mb-3 xs:mb-4">
                  <Badge className="bg-green-100 text-green-800 border-green-200 text-xs xs:text-sm">
                    🟢 Subpanel System
                  </Badge>
                </div>

                <div className="space-y-2 xs:space-y-3">
                  {process.modern.steps.map((step, stepIndex) => (
                    <div key={stepIndex} className="flex items-start gap-2 xs:gap-3">
                      <div className="w-5 h-5 xs:w-6 xs:h-6 rounded-full bg-green-100 text-green-600 text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">
                        {stepIndex + 1}
                      </div>
                      <div className="text-xs xs:text-sm">{step}</div>
                    </div>
                  ))}
                </div>

                {process.modern.note && (
                  <div className="text-xs text-green-700 bg-green-50 p-2 rounded border border-green-200">
                    {process.modern.note}
                  </div>
                )}

                <div className="pt-3 xs:pt-4 border-t flex flex-col xs:flex-row xs:justify-between gap-2 text-xs xs:text-sm">
                  <div>
                    <span className="font-medium">Time:</span> {process.modern.time}
                  </div>
                  <div>
                    <span className="font-medium">Satisfaction:</span> {process.modern.satisfaction}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TransparencyComparison() {
  const features = [
    {
      feature: 'Meeting Records',
      legacy: 'Summary minutes, weeks later',
      modern: 'Real-time decision logging',
      improvement: 'Instant access',
    },
    {
      feature: 'Financial Transparency',
      legacy: 'Quarterly reports',
      modern: 'Live budget tracking',
      improvement: 'Always up-to-date',
    },
    {
      feature: 'Voting Records',
      legacy: '"The board voted 4-1"',
      modern: 'Individual voting history available',
      improvement: 'Full accountability',
    },
    {
      feature: 'Decision Rationale',
      legacy: 'Rarely documented',
      modern: 'Required explanation for every choice',
      improvement: 'No more guessing',
    },
    {
      feature: 'Community Input',
      legacy: 'Annual meetings only',
      modern: 'Digital feedback anytime',
      improvement: 'Voice heard daily',
    },
    {
      feature: 'Appeal Process',
      legacy: 'Unclear, political',
      modern: 'Clear escalation path',
      improvement: 'Fair resolution',
    },
  ];

  return (
    <div className="space-y-3 xs:space-y-4">
      {features.map((feature, index) => (
        <Card key={index}>
          <CardContent className="p-3 xs:p-4">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 xs:gap-4 items-start">
              <div className="font-semibold text-sm xs:text-base mb-2 lg:mb-0">
                {feature.feature}
              </div>

              <div className="text-xs xs:text-sm text-red-700 bg-red-50 p-2 xs:p-3 rounded border border-red-200">
                <div className="font-medium text-red-800 mb-1">Legacy</div>
                {feature.legacy}
              </div>

              <div className="text-xs xs:text-sm text-green-700 bg-green-50 p-2 xs:p-3 rounded border border-green-200">
                <div className="font-medium text-green-800 mb-1">Modern</div>
                {feature.modern}
              </div>

              <div className="text-center lg:text-center">
                <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-xs">
                  {feature.improvement}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ScenarioComparison({
  activeScenario,
  onScenarioChange,
}: {
  activeScenario: string;
  onScenarioChange: (scenario: string) => void;
}) {
  const scenarios = {
    budget: {
      title: 'Annual Budget Crisis',
      legacy: {
        timeline: '20 weeks',
        result: '5-month process, community divisiveness, rushed implementation',
        steps: [
          'Week 1-4: Budget committee meetings (4 times, disagreements)',
          'Week 5-8: Revised proposals, more committee debates',
          'Week 9: Board meeting #1 - sends budget back to committee',
          'Week 10-12: More revisions, political maneuvering',
          'Week 13: Board meeting #2 - narrow approval (3-2 vote)',
          'Week 14-16: Community notification, resident complaints',
          'Week 17: Resident meeting - challenges and appeals',
          'Week 18-20: Final revisions and approval',
        ],
      },
      modern: {
        timeline: '5 weeks',
        result: '5-week process, community confidence, smooth implementation',
        steps: [
          'Week 1: Community voices budget concerns and priorities',
          'Week 2: Community subpanel reviews and discusses options',
          'Week 3: Directors draft budget resolution based on community input',
          'Week 4: Community Subpanel votes on resolution (Community Elect stays neutral)',
          'Week 5: Implementation begins with community override options available*',
        ],
      },
    },
    governance: {
      title: 'Choosing Your Governance Model',
      legacy: {
        timeline: 'Fixed forever',
        result: 'Stuck with inherited structure whether it works or not',
        steps: [
          'Community inherits decades-old bylaws',
          "Structure doesn't fit community needs",
          'No flexibility to adapt or improve',
          'Resident frustration grows over time',
          'Major bylaw changes require expensive legal process',
        ],
      },
      modern: {
        timeline: 'Configurable anytime',
        result: 'Community chooses and can adapt governance to their preferences',
        steps: [
          'Community selects preferred decision-making model',
          'Options: Community Elect override, community override, popular vote',
          "System adapts to community's chosen governance style",
          'Can switch models based on community feedback',
          'Technology handles the complexity automatically*',
        ],
      },
    },
    emergency: {
      title: 'Emergency Road Washout Response',
      legacy: {
        timeline: 'Weeks of delay',
        result: 'Residents stranded, community anger, expensive emergency repairs',
        steps: [
          'Major storm washes out main access road to 50 properties',
          'Affected residents call board president directly',
          'President says "need to call emergency board meeting"',
          'Board members unavailable - some out of town, others at work',
          'Takes 4 days to get 3 board members on a conference call',
          'Debate over contractor selection and who pays',
          'Residents cut off from emergency services meanwhile',
        ],
      },
      modern: {
        timeline: 'Same day',
        result: 'Road access restored quickly, community confidence maintained',
        steps: [
          'Storm damage reported through digital emergency system',
          'Community subpanel automatically notified of road emergency',
          'Director activates pre-approved emergency road repair authority',
          'Contractor contacted immediately using pre-vetted vendor list',
          'If Director stalls: Community can override & replace within 24 hours*',
          'All 406 parcels notified with repair timeline and cost allocation',
        ],
      },
    },
  };

  const scenarioData = scenarios[activeScenario as keyof typeof scenarios];

  return (
    <div className="space-y-4 xs:space-y-6">
      <div className="flex gap-1 xs:gap-2 justify-center flex-wrap px-2">
        <Button
          variant={activeScenario === 'budget' ? 'default' : 'outline'}
          onClick={() => onScenarioChange('budget')}
          size="sm"
          className="text-xs xs:text-sm"
        >
          Budget Process
        </Button>
        <Button
          variant={activeScenario === 'governance' ? 'default' : 'outline'}
          onClick={() => onScenarioChange('governance')}
          size="sm"
          className="text-xs xs:text-sm"
        >
          <span className="hidden xs:inline">Governance Models</span>
          <span className="xs:hidden">Models</span>
        </Button>
        <Button
          variant={activeScenario === 'emergency' ? 'default' : 'outline'}
          onClick={() => onScenarioChange('emergency')}
          size="sm"
          className="text-xs xs:text-sm"
        >
          <span className="hidden xs:inline">Emergency Response</span>
          <span className="xs:hidden">Emergency</span>
        </Button>
      </div>

      <Card>
        <CardHeader className="p-3 xs:p-4 sm:p-6">
          <CardTitle className="text-lg xs:text-xl text-center">{scenarioData.title}</CardTitle>
        </CardHeader>
        <CardContent className="p-3 xs:p-4 sm:p-6 pt-0">
          <div className="grid lg:grid-cols-2 gap-4 xs:gap-6 sm:gap-8">
            {/* Legacy Process */}
            <div className="space-y-3 xs:space-y-4">
              <div className="text-center">
                <Badge className="bg-red-100 text-red-800 border-red-200 mb-3 xs:mb-4 text-xs xs:text-sm">
                  🔴 Traditional HOA Process
                </Badge>
                <div className="text-xl xs:text-2xl font-bold text-red-600 mb-2">
                  {scenarioData.legacy.timeline}
                </div>
              </div>

              <div className="space-y-2 xs:space-y-3">
                {scenarioData.legacy.steps.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 xs:gap-3 p-2 xs:p-3 rounded bg-red-50 border border-red-200"
                  >
                    <div className="w-5 h-5 xs:w-6 xs:h-6 rounded-full bg-red-600 text-white text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">
                      {index + 1}
                    </div>
                    <div className="text-xs xs:text-sm text-red-800">{step}</div>
                  </div>
                ))}
              </div>

              <div className="p-3 xs:p-4 bg-red-100 border border-red-200 rounded">
                <div className="font-semibold text-red-800 mb-2 text-sm xs:text-base">Result:</div>
                <div className="text-xs xs:text-sm text-red-700">{scenarioData.legacy.result}</div>
              </div>
            </div>

            {/* Modern Process */}
            <div className="space-y-3 xs:space-y-4">
              <div className="text-center">
                <Badge className="bg-green-100 text-green-800 border-green-200 mb-3 xs:mb-4 text-xs xs:text-sm">
                  🟢 Subpanel System Process
                </Badge>
                <div className="text-xl xs:text-2xl font-bold text-green-600 mb-2">
                  {scenarioData.modern.timeline}
                </div>
              </div>

              <div className="space-y-2 xs:space-y-3">
                {scenarioData.modern.steps.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 xs:gap-3 p-2 xs:p-3 rounded bg-green-50 border border-green-200"
                  >
                    <div className="w-5 h-5 xs:w-6 xs:h-6 rounded-full bg-green-600 text-white text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">
                      {index + 1}
                    </div>
                    <div className="text-xs xs:text-sm text-green-800">{step}</div>
                  </div>
                ))}
              </div>

              <div className="p-3 xs:p-4 bg-green-100 border border-green-200 rounded">
                <div className="font-semibold text-green-800 mb-2 text-sm xs:text-base">
                  Result:
                </div>
                <div className="text-xs xs:text-sm text-green-700">
                  {scenarioData.modern.result}
                </div>
                {activeScenario === 'governance' && (
                  <div className="mt-3 text-xs text-green-600 bg-green-50 p-2 rounded">
                    <strong>Available Models:</strong> Some communities prefer Community Elect as
                    final overrider, others want community subpanel as typical voting decision
                    maker, and some choose community popular vote where community subpanel drafts
                    and community decides. The system adapts to your preference.
                  </div>
                )}
                {activeScenario === 'emergency' && (
                  <div className="mt-3 text-xs text-green-600 bg-green-50 p-2 rounded">
                    <strong>Community Override Power:</strong> If a Director fails to act on an
                    approved emergency repair, the community can digitally vote to remove them and
                    elect a replacement within 24 hours. This ensures emergency responses never get
                    blocked by one person's inaction.
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
